/**
 * ══════════════════════════════════════════════════════════════════════
 * HOMEBASE HELP MODE -- EINE GLUEHBIRNE JE SEITE
 * ══════════════════════════════════════════════════════════════════════
 *
 * WAS SICH GEGENUEBER DEM PILOTEN AENDERT, UND WARUM.
 *
 * Der Pilot setzte eine Gluehbirne NEBEN jeden erklaerungsbeduerftigen
 * Bereich. Das funktioniert bei dreien und zerfaellt bei zehn: der
 * Benutzer muss erst die Gluehbirnen suchen, bevor er die Hilfe
 * findet, und jede einzelne ist ein kleines Stueck Unruhe in einer
 * Ueberschrift.
 *
 * DAS ENDGUELTIGE MUSTER IST EIN SCHALTER JE SEITE:
 *
 *   AUS   graue Kontur, die Seite ist ruhig, kein Zusatztext
 *   AN    gelb gefuellt, und ALLE Hinweise dieser Seite erscheinen
 *
 * GELB IST HIER KEINE FACHSTATUSFARBE. Es bedeutet ausschliesslich
 * „der Hilfemodus ist eingeschaltet" -- deshalb steht es auch nur am
 * Schalter und nirgends sonst. HOMEBASE-Gruen bleibt die
 * Akzentfarbe des Fachs.
 *
 * DIE TEXTE BLEIBEN, WO SIE WAREN: im Woerterbuch, unter
 * `help.<bereich>.title` und `.body`, ausgeschrieben und nicht
 * zusammengebaut. Die drei Pilottexte sind unveraendert
 * weiterverwendet.
 *
 * ES IST KEINE ZWEITE HILFEARCHITEKTUR. Dieselbe Datei, dieselben
 * Schluessel, dieselbe i18n -- hinzugekommen ist ein Zustand, den
 * sich die Seite und ihre Hinweise teilen.
 */
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useI18n } from "../i18n";
import "./Hilfe.css";

/**
 * Die Bereiche, die eine Hilfe haben.
 *
 * EINE GESCHLOSSENE LISTE, UND DAS IST ABSICHT. Sie ist die Antwort
 * auf „an jedes Feld einen Text": wer einen hinzufuegen will, traegt
 * ihn hier ein und sieht dabei, wie viele es schon sind. Der
 * Typpruefer verlangt ausserdem, dass es den Text wirklich gibt --
 * ein Hinweis, der ins Leere zeigt, entsteht so gar nicht erst.
 *
 * WOFUER ES AUSDRUECKLICH KEINEN TEXT GIBT: Bestellnummer, Waehrung,
 * Notiz, Artikelnummer. Wer die nicht versteht, dem hilft ein Satz
 * daneben auch nicht.
 */
const HILFETEXTE = {
  /* Produktion & Logistik -- Uebersicht */
  timeline: ["help.timeline.title", "help.timeline.body"],
  "ov.cnwahl": ["help.ov.cnwahl.title", "help.ov.cnwahl.body"],
  "ov.ware": ["help.ov.ware.title", "help.ov.ware.body"],
  /* Sendungsakte */
  "cn.transport": ["help.cn.transport.title", "help.cn.transport.body"],
  "cn.arbeitszone": ["help.cn.arbeitszone.title", "help.cn.arbeitszone.body"],
  "cn.planist": ["help.cn.planist.title", "help.cn.planist.body"],
  /* Bestellakte */
  "po.documents": ["help.po.documents.title", "help.po.documents.body"],
  "po.incoterm": ["help.po.incoterm.title", "help.po.incoterm.body"],
  "po.leadtime": ["help.po.leadtime.title", "help.po.leadtime.body"],
  "po.payments": ["help.po.payments.title", "help.po.payments.body"],
} as const;

export const HILFEBEREICHE = Object.keys(HILFETEXTE) as readonly Hilfebereich[];
export type Hilfebereich = keyof typeof HILFETEXTE;

/**
 * Der geteilte Zustand einer Seite: ist der Hilfemodus an?
 *
 * Ein Context und kein Zustand je Hinweis -- sonst waere „ein
 * Schalter je Seite" eine Behauptung und keine Tatsache.
 */
const HilfeKontext = createContext<{ an: boolean; umschalten: () => void } | null>(null);

/**
 * Der Rahmen einer Seite mit Hilfe.
 *
 * Steht er nicht da, zeigt `HilfeHinweis` nichts und `HilfeSchalter`
 * erscheint gar nicht erst. Eine Seite ohne Rahmen ist damit einfach
 * eine Seite ohne Hilfe -- kein Fehler, kein Sonderfall.
 */
export function HilfeRahmen({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [an, setAn] = useState(false);
  const umschalten = useCallback(() => setAn((a) => !a), []);
  const wert = useMemo(() => ({ an, umschalten }), [an, umschalten]);
  return (
    <HilfeKontext.Provider value={wert}>
      <div className={an ? "hilfe-seite hilfe-seite--an" : "hilfe-seite"}
        data-testid="hilfe-seite" data-hilfe={an ? "an" : "aus"}>
        {children}
      </div>
    </HilfeKontext.Provider>
  );
}

/**
 * DER EINE SCHALTER.
 *
 * Ein `<button>` -- damit mit Tabulator erreichbar und mit Leertaste
 * und Eingabetaste bedienbar, ohne eine Zeile Tastaturcode. Kein
 * Hover: was man nur findet, wenn man zufaellig darueberfaehrt, gibt
 * es auf einem Tastgeraet nicht.
 */
export function HilfeSchalter(): React.JSX.Element | null {
  const k = useContext(HilfeKontext);
  const i18n = useI18n();
  if (k === null) return null;
  return (
    <button type="button"
      className={k.an ? "hilfe__schalter hilfe__schalter--an" : "hilfe__schalter"}
      data-testid="hilfe-schalter" aria-pressed={k.an}
      aria-label={i18n.t(k.an ? "help.modeOff" : "help.modeOn")}
      title={i18n.t(k.an ? "help.modeOff" : "help.modeOn")}
      onClick={k.umschalten}>
      {/* Das Zeichen ist Schmuck -- die Bedeutung steht im `aria-label`.
          Ein Symbol allein ist keine Beschriftung. */}
      <span aria-hidden="true">💡</span>
    </button>
  );
}

/**
 * Ein Hinweis -- sichtbar nur im Hilfemodus.
 *
 * Er steht IM Fluss der Seite und nicht als Blase darueber: im
 * Hilfemodus sollen alle Hinweise gleichzeitig lesbar sein, und
 * zehn uebereinanderliegende Blasen waeren das Gegenteil davon.
 */
export function HilfeHinweis({ bereich }: { bereich: Hilfebereich }): React.JSX.Element | null {
  const k = useContext(HilfeKontext);
  const i18n = useI18n();
  if (k === null || !k.an) return null;
  const [titelKey, textKey] = HILFETEXTE[bereich];
  return (
    <aside className="hilfe__hinweis" data-testid="hilfe-hinweis" data-bereich={bereich}
      role="note">
      <span className="hilfe__titel">{i18n.t(titelKey)}</span>
      <span className="hilfe__body">{i18n.t(textKey)}</span>
    </aside>
  );
}
